import React, { useEffect } from 'react';
import {
  ConfirmationTypes,
  IAdminNotesResponse,
  IAdminPharmacyNotesResponse,
  adminActions,
  adminAllNotes,
  adminGetAllNotes,
  adminGetAllPharmacyNotes,
  adminPharmacyAllNotes,
  deletePharmacistAdminNote,
  deletePharmacyAdminNote,
  resetStatus,
  successOrSelector,
} from '@pharmaplan/common';
import {
  adminNoteHeader,
  notesRowConfig,
  pharmacyNotesRowConfig,
} from '../../../components/Admin/AdminNotes/helper';
import { IDynamicTableObject } from '../../../components/DynamicTable/types';
import { AdminNotesType, ScreenTypes } from '../../../helpers/Constants';
import { useAppSelector } from '../../useAppSelector';
import { useAppDispatch } from '../../useAppDispatch';
import { renderScreen, setFooterShow } from '../../../actions/drawerActions';
import { resetDialog, setDialog } from '../../../reducers/dialogReducer';
import strings from '../../../localization';
import AdminUpdateNote from '../../../components/Admin/AdminNotes/AdminCreateUpdateNotes/AdminUpdateNote';
import ConfirmationModal from '../../../components/Modals/ConfirmationModal';

const { pharmacist, pharmacy } = AdminNotesType;

const useAdminNotes = (id: string, notesType: AdminNotesType) => {
  const dispatch = useAppDispatch();

  const deleteSuccess = useAppSelector((state) =>
    successOrSelector(
      [
        adminActions.deletePharmacistAdminNote,
        adminActions.deletePharmacyAdminNote,
      ],
      state,
    ));

  const pharmacyAllNotes = useAppSelector(adminPharmacyAllNotes);
  const pharmacistAllNotes = useAppSelector(adminAllNotes);

  const allNotes = {
    [pharmacist]: pharmacistAllNotes,
    [pharmacy]: pharmacyAllNotes,
  };

  const isPharmacist = notesType === pharmacist;

  const deleteNoteConfig = {
    [pharmacist]: (noteId: string) =>
      () => {
        dispatch(deletePharmacistAdminNote(noteId));
      },
    [pharmacy]: (noteId: string) =>
      () => {
        dispatch(deletePharmacyAdminNote(noteId));
      },
  };

  const deleteConfirmation = (noteType: AdminNotesType) =>
    (noteId: string) =>
      () => {
        dispatch(
          setDialog({
            showCloseButton: false,
            heading: {
              title: '',
            },
            Component: (
              <ConfirmationModal
                confirmAction={deleteNoteConfig[noteType](noteId)}
                type={ConfirmationTypes.deleteEntry}
              />
            ),
          }),
        );
      };

  const updateNote = (noteId: string) =>
    () => {
      dispatch(
        setDialog({
          Component: (
            <AdminUpdateNote id={id} noteId={noteId} notesType={notesType} />
          ),
          showCloseButton: true,
          heading: {
            title: strings.updateNote,
          },
        }),
      );
    };

  const rowsConfig = {
    [pharmacist]: notesRowConfig(
      allNotes[notesType] as IAdminNotesResponse[],
      updateNote,
      deleteConfirmation(pharmacist),
    ),
    [pharmacy]: pharmacyNotesRowConfig(
      allNotes[notesType] as IAdminPharmacyNotesResponse[],
      updateNote,
      deleteConfirmation(pharmacy),
    ),
  };

  const table: IDynamicTableObject = {
    headerBar: [],
    headers: adminNoteHeader(),
    rows: rowsConfig[notesType],
  };

  const ratingGoBack = () => {
    dispatch(
      renderScreen({
        screenType: ScreenTypes.pharmacistList,
        screenNumber: 1,
        pharmacistId: id,
      }),
    );
  };

  const initCall = () => {
    if (isPharmacist) {
      dispatch(adminGetAllNotes(id));
    } else {
      dispatch(adminGetAllPharmacyNotes(id));
    }
  };

  useEffect(() => {
    initCall();
    dispatch(setFooterShow(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (deleteSuccess) {
      initCall();
      dispatch(resetStatus([adminActions.deletePharmacistAdminNote, adminActions.deletePharmacyAdminNote]));
      dispatch(resetDialog());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteSuccess]);

  return { ratingGoBack, table, data: allNotes[notesType], isPharmacist };
};

export default useAdminNotes;
